/* ==========================================================================
   Tack 2026 motion runtime
   - Reveal on enter (IntersectionObserver)
   - Scroll scrub: writes --p (0..1) on [data-t26-scrub]
   - Parallax: writes --y (px) on [data-t26-para]
   - Sticky step activation for feature scrollers
   - Count-up stats
   - Pointer tilt on cards
   - No stranded words (runs even with reduced motion)
   All work happens inside one rAF loop. Nothing animates off-screen.
   ========================================================================== */
(function () {
  'use strict';
  if (window.__t26Init) return;
  window.__t26Init = true;

  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var isCoarse = window.matchMedia('(pointer: coarse)').matches;

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    /* ---------------- no stranded words ----------------
       Joins the last two words of every block of copy with a non-breaking
       space, so a line never ends with one word on its own. Skipped when the
       pair would not fit the box (narrow table cells), and on strings of two
       words or fewer. Headings split into .t26-mask word spans keep their last
       two spans together in a no-wrap span. */
    function noWidows(root) {
      var NB = ' ';
      var sel = '.t26 h1, .t26 h2, .t26 h3, .t26 h4, .t26 p, .t26 li, .t26 blockquote, .t26 figcaption, .t26 dd, .t26 th, .t26 td';
      (root || document).querySelectorAll(sel).forEach(function (el) {
        if (el.hasAttribute('data-t26-nw')) return;
        if (el.querySelector('p, li, h1, h2, h3, h4, blockquote, ul, ol, div')) return;
        var words = el.textContent.trim().split(/\s+/);
        if (words.length < 3) return;
        el.setAttribute('data-t26-nw', '');
        var undo = null;
        var masks = el.querySelectorAll('.t26-mask');
        if (masks.length >= 3) {
          /* Each word is an inline-block, and a line can break beside an
             inline-block even across a non-breaking space. So the last two
             word spans go inside one no-wrap span instead. */
          var lastMask = masks[masks.length - 1], prevMask = masks[masks.length - 2];
          if (prevMask.parentNode === lastMask.parentNode) {
            var pair = document.createElement('span');
            pair.style.whiteSpace = 'nowrap';
            prevMask.parentNode.insertBefore(pair, prevMask);
            var nodeNow = prevMask;
            while (nodeNow) { var nx = nodeNow.nextSibling; pair.appendChild(nodeNow); if (nodeNow === lastMask) break; nodeNow = nx; }
            undo = function () { while (pair.firstChild) pair.parentNode.insertBefore(pair.firstChild, pair); pair.remove(); };
          }
        } else {
          var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
          var n, nodes = [];
          while ((n = walker.nextNode())) nodes.push(n);
          for (var i = nodes.length - 1; i >= 0; i--) {
            var t = nodes[i].textContent;
            var trimmed = t.replace(/\s+$/, '');
            var at = trimmed.search(/\s\S+$/);
            if (at > 0 && /\S/.test(trimmed.slice(0, at))) {
              var node = nodes[i], orig = t;
              /* Hyphens inside the joined pair become non-breaking too, or
                 "built-in barometer." still breaks after "built-". */
              var head = trimmed.slice(0, at), ps = head.search(/\S+$/);
              node.textContent = head.slice(0, ps) + head.slice(ps).replace(/-/g, '‑') + NB +
                trimmed.slice(at + 1).replace(/-/g, '‑') + t.slice(trimmed.length);
              undo = function () { node.textContent = orig; };
              break;
            }
            if (/\S/.test(t)) break;
          }
        }
        if (undo && el.scrollWidth > el.clientWidth + 1) { undo(); el.setAttribute('data-t26-nw', 'skip'); }
      });
    }
    noWidows();
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { noWidows(); });

    if (reduce.matches) {
      document.querySelectorAll('[data-t26-reveal]').forEach(function (el) { el.classList.add('is-in'); });
      document.querySelectorAll('.t26-step').forEach(function (el) { el.classList.add('is-active'); });
      document.querySelectorAll('[data-t26-count]').forEach(function (el) { el.textContent = el.getAttribute('data-t26-count'); });
      return;
    }

    /* ---------------- reveal ---------------- */

    var revealObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        revealObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.08 });

    document.querySelectorAll('[data-t26-reveal]').forEach(function (el, i) {
      if (!el.style.getPropertyValue('--t26-delay')) {
        var group = el.getAttribute('data-t26-group');
        if (group) el.style.setProperty('--t26-delay', (parseInt(group, 10) * 90) + 'ms');
      }
      revealObserver.observe(el);
    });

    document.querySelectorAll('.t26-maskline').forEach(function (line) {
      var words = line.querySelectorAll('.t26-mask > span');
      words.forEach(function (w, i) { w.style.transitionDelay = (i * 75) + 'ms'; });
    });

    var lineObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        lineObserver.unobserve(entry.target);
      });
    }, { threshold: 0.2 });
    document.querySelectorAll('.t26-maskline').forEach(function (l) { lineObserver.observe(l); });

    /* ---------------- count up ---------------- */

    var countObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        countObserver.unobserve(el);
        var target = parseFloat(el.getAttribute('data-t26-count'));
        var dur = 1500;
        var start = performance.now();
        (function tick(now) {
          /* rAF can hand back a timestamp slightly earlier than start, which
             made t negative and briefly printed "-0". Clamp to 0..1. */
          var t = Math.max(0, Math.min((now - start) / dur, 1));
          var eased = 1 - Math.pow(1 - t, 4);
          el.textContent = Math.round(target * eased).toLocaleString();
          if (t < 1) requestAnimationFrame(tick);
        })(start);
      });
    }, { threshold: 0.5 });
    document.querySelectorAll('[data-t26-count]').forEach(function (el) { countObserver.observe(el); });

    /* ---------------- scroll driven ---------------- */

    var scrubs = [].slice.call(document.querySelectorAll('[data-t26-scrub]'));
    var paras  = [].slice.call(document.querySelectorAll('[data-t26-para]'));
    var stepSets = [].slice.call(document.querySelectorAll('[data-t26-steps]'));

    var active = new Set();
    var nearObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) active.add(entry.target);
        else active.delete(entry.target);
      });
      request();
    }, { rootMargin: '25% 0px 25% 0px' });

    scrubs.concat(paras).concat(stepSets).forEach(function (el) { nearObserver.observe(el); });

    var ticking = false;
    function request() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(frame);
    }

    function frame() {
      ticking = false;
      var vh = window.innerHeight;

      scrubs.forEach(function (el) {
        if (!active.has(el)) return;
        var r = el.getBoundingClientRect();
        var total = r.height - vh;
        var p = total > 0 ? (-r.top) / total : (r.top < vh ? 1 : 0);
        p = p < 0 ? 0 : (p > 1 ? 1 : p);
        el.style.setProperty('--p', p.toFixed(4));
      });

      if (!isCoarse) {
        paras.forEach(function (el) {
          if (!active.has(el)) return;
          var speed = parseFloat(el.getAttribute('data-t26-para')) || 0.12;
          var r = el.getBoundingClientRect();
          var centre = r.top + r.height / 2;
          var offset = (centre - vh / 2) * speed;
          el.style.setProperty('--y', offset.toFixed(2) + 'px');
        });
      }

      stepSets.forEach(function (set) {
        if (!active.has(set)) return;
        var steps = set.querySelectorAll('.t26-step');
        var best = null, bestDist = Infinity;
        steps.forEach(function (s) {
          var r = s.getBoundingClientRect();
          var d = Math.abs((r.top + r.height / 2) - vh / 2);
          if (d < bestDist) { bestDist = d; best = s; }
        });
        steps.forEach(function (s) {
          var on = s === best;
          s.classList.toggle('is-active', on);
          if (on) {
            var imgId = set.getAttribute('data-t26-steps');
            var target = imgId ? document.getElementById(imgId) : null;
            var src = s.getAttribute('data-t26-shot');
            if (target && src && target.getAttribute('src') !== src) {
              target.style.opacity = '0';
              setTimeout(function () {
                target.setAttribute('src', src);
                target.style.opacity = '1';
              }, 220);
            }
          }
        });
      });
    }

    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request, { passive: true });
    frame();

    /* ---------------- pointer tilt ---------------- */

    if (!isCoarse) {
      document.querySelectorAll('[data-t26-tilt]').forEach(function (card) {
        var max = parseFloat(card.getAttribute('data-t26-tilt')) || 5;
        card.addEventListener('pointermove', function (e) {
          var r = card.getBoundingClientRect();
          var px = (e.clientX - r.left) / r.width - 0.5;
          var py = (e.clientY - r.top) / r.height - 0.5;
          card.style.setProperty('--ry', (px * max * 2).toFixed(2) + 'deg');
          card.style.setProperty('--rx', (-py * max * 2).toFixed(2) + 'deg');
        });
        card.addEventListener('pointerleave', function () {
          card.style.setProperty('--ry', '0deg');
          card.style.setProperty('--rx', '0deg');
        });
      });
    }
  });
})();
